/**
 * Reveal on open: when the size list opens, the page glides (rAF, eased, ~0.45-0.9s) so
 * the "Size:" row rests just under the pinned header and the whole list is on screen,
 * instead of opening below the fold. One animation owns the page at a time, any touch,
 * wheel or scroll key hands control straight back to the shopper, and reduced-motion
 * users get an instant jump instead of the glide.
 */
const SizeDropdownReveal = (() => {
  const GAP = 16; // space between the pinned header and the "Size:" row
  const TOLERANCE = 6; // already this close to the spot: leave the page where it is
  const PANEL_GAP = 16; // room kept under the list on short screens
  const PANEL_MIN = 180; // never shrink the list below about three rows
  const MIN_MS = 450;
  const MAX_MS = 900;
  const SCROLL_KEYS = [
    "ArrowUp",
    "ArrowDown",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    " ",
    "Spacebar",
  ];

  const root = document.documentElement;
  const reducedMotion = window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

  // cubic-bezier(0.4, 0, 0.2, 1): a gentle start and a long, soft landing.
  const ease = ((x1, y1, x2, y2) => {
    const cx = 3 * x1;
    const bx = 3 * (x2 - x1) - cx;
    const ax = 1 - cx - bx;
    const cy = 3 * y1;
    const by = 3 * (y2 - y1) - cy;
    const ay = 1 - cy - by;
    const curveX = (t) => ((ax * t + bx) * t + cx) * t;
    const curveY = (t) => ((ay * t + by) * t + cy) * t;
    const slopeX = (t) => (3 * ax * t + 2 * bx) * t + cx;

    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let t = x;
      for (let i = 0; i < 8; i++) {
        const error = curveX(t) - x;
        if (Math.abs(error) < 1e-6) return curveY(t);
        const slope = slopeX(t);
        if (Math.abs(slope) < 1e-6) break;
        t -= error / slope;
      }
      let low = 0;
      let high = 1;
      t = x;
      for (let i = 0; i < 30; i++) {
        const value = curveX(t);
        if (Math.abs(value - x) < 1e-6) break;
        if (value < x) low = t;
        else high = t;
        t = (low + high) / 2;
      }
      return curveY(t);
    };
  })(0.4, 0, 0.2, 1);

  const isRendered = (el) =>
    !!el && el.isConnected && el.getClientRects().length > 0;
  const maxScroll = () =>
    Math.max(
      0,
      (document.scrollingElement || root).scrollHeight - window.innerHeight,
    );
  const clampScroll = (y) => Math.max(0, Math.min(maxScroll(), y));

  // How far down a pinned element will cover the viewport once the page rests at the picker.
  const pinnedBottom = (el, owner, anchorTop, viewportHeight) => {
    const style = getComputedStyle(el);
    const position = style.position;
    const fixed = position === "fixed";
    if (!fixed && position.indexOf("sticky") === -1) return 0;
    if (style.visibility === "hidden" || el.contains(owner)) return 0;

    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.height >= viewportHeight * 0.5) return 0;

    // Fixed: only bars anchored to the top edge. Bottom docks and slid-away headers don't count.
    if (fixed) return rect.top <= 2 && rect.bottom > 0 ? rect.bottom : 0;

    // Sticky: it will rest at its `top` offset, unless its container ends above the picker.
    const offset = parseFloat(style.top);
    if (!Number.isFinite(offset) || offset < 0 || offset > viewportHeight * 0.3)
      return 0;
    let container = el.parentElement;
    while (
      container &&
      container !== document.body &&
      getComputedStyle(container).display === "contents"
    ) {
      container = container.parentElement; // wrappers with display:contents have no box of their own
    }
    if (
      container &&
      container !== document.body &&
      container.getBoundingClientRect().bottom < anchorTop
    )
      return 0;
    return offset + rect.height;
  };

  // Height of whatever stays pinned to the top of the viewport (announcement bar + sticky
  // header here). Measured rather than hardcoded so it follows the real header on every
  // device, including a header that hasn't reached its pinned spot yet near the page top.
  // Returns a function that re-measures just the bars it found, cheap enough per frame, so
  // a header that changes height mid-glide is still cleared exactly.
  const pinnedTopInset = (owner, anchor) => {
    const pinned = [];
    if (document.elementsFromPoint) {
      const viewportHeight = window.innerHeight;
      const anchorTop = anchor.getBoundingClientRect().top;
      const width = root.clientWidth || window.innerWidth;
      const xs = [Math.round(width * 0.3), Math.round(width * 0.7)];
      const limit = Math.min(260, viewportHeight * 0.45);
      const seen = new Set();

      for (let y = 1; y <= limit; y += 12) {
        xs.forEach((x) => {
          document.elementsFromPoint(x, y).forEach((hit) => {
            // Walk up too: a sticky wrapper may have pointer-events:none and never be hit itself.
            for (
              let el = hit;
              el && el !== document.body && el !== root;
              el = el.parentElement
            ) {
              if (seen.has(el)) break;
              seen.add(el);
              if (pinnedBottom(el, owner, anchorTop, viewportHeight) > 0)
                pinned.push(el);
            }
          });
        });
      }
    }

    return () => {
      const viewportHeight = window.innerHeight;
      const anchorTop = anchor.getBoundingClientRect().top;
      let inset = 0;
      pinned.forEach((el) => {
        if (!el.isConnected) return;
        const bottom = pinnedBottom(el, owner, anchorTop, viewportHeight);
        if (bottom > inset) inset = bottom;
      });
      return inset;
    };
  };

  // Programmatic scrolls must be instant for the frame loop to own the motion, even if the
  // theme ever sets `scroll-behavior: smooth` on <html>. Returns a function that undoes it.
  const forceInstantScroll = () => {
    const value = root.style.getPropertyValue("scroll-behavior");
    const priority = root.style.getPropertyPriority("scroll-behavior");
    root.style.setProperty("scroll-behavior", "auto", "important");
    return () => {
      if (value) root.style.setProperty("scroll-behavior", value, priority);
      else root.style.removeProperty("scroll-behavior");
    };
  };

  let active = null;

  const stop = (owner) => {
    if (active && (!owner || active.owner === owner)) active.end();
  };

  const glide = (owner, resolveTarget) => {
    stop();

    const startY = window.pageYOffset;
    let target = resolveTarget();
    if (target === null) return;

    const restore = forceInstantScroll();
    // An instant scroll aborts any smooth scroll already in flight (the sticky bar's
    // "Select a size" starts one right before it opens this list), so only one motion runs.
    window.scrollTo(0, startY);

    if (reducedMotion && reducedMotion.matches) {
      window.scrollTo(0, target);
      restore();
      return;
    }

    const duration = Math.min(
      MAX_MS,
      Math.max(MIN_MS, 320 + Math.abs(target - startY) * 0.45),
    );
    const run = { owner, end: null };
    let startTime = 0;
    let frame = 0;

    // The shopper touching, wheeling or key-scrolling takes the page back immediately.
    const onInput = (event) => {
      if (
        event.type === "keydown" &&
        (event.defaultPrevented || SCROLL_KEYS.indexOf(event.key) === -1)
      )
        return;
      run.end();
    };
    const listen = (add) => {
      const method = add ? "addEventListener" : "removeEventListener";
      window[method]("touchstart", onInput, { capture: true, passive: true });
      window[method]("pointerdown", onInput, { capture: true, passive: true });
      window[method]("wheel", onInput, { capture: true, passive: true });
      window[method]("keydown", onInput);
    };

    run.end = () => {
      if (active !== run) return;
      active = null;
      cancelAnimationFrame(frame);
      listen(false);
      restore();
    };

    const step = (now) => {
      if (!startTime) startTime = now;
      const progress = Math.min(1, (now - startTime) / duration);
      // Re-read the target every frame so late layout shifts (fit widget, images) are absorbed.
      const next = resolveTarget();
      if (next !== null) target = next;
      window.scrollTo(0, startY + (target - startY) * ease(progress));
      if (progress < 1) frame = requestAnimationFrame(step);
      else run.end();
    };

    active = run;
    listen(true);
    frame = requestAnimationFrame(step);
  };

  return {
    GAP,
    TOLERANCE,
    PANEL_GAP,
    PANEL_MIN,
    isRendered,
    clampScroll,
    pinnedTopInset,
    glide,
    stop,
  };
})();

class SizeDropdown extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector("[data-sd-trigger]");
    this.valueEl = this.querySelector("[data-sd-label]");
    this.panel = this.querySelector("[data-sd-panel]");
    this.fieldset = this.closest("fieldset");

    if (!this.trigger || !this.valueEl || !this.panel || !this.fieldset) return;

    this.optionEls = Array.from(this.panel.querySelectorAll('[role="option"]'));
    this.placeholder = this.dataset.placeholder || "";
    this.activeIndex = -1;
    this.isOpen = false;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onProductSwitched = this.onProductSwitched.bind(this);
    this.syncFromRadios = this.syncFromRadios.bind(this);

    this.trigger.addEventListener("click", this.onTriggerClick.bind(this));
    this.trigger.addEventListener("keydown", this.onTriggerKeydown.bind(this));
    this.panel.addEventListener("click", this.onPanelClick.bind(this));
    this.panel.addEventListener("keydown", this.onPanelKeydown.bind(this));
    this.panel.addEventListener("mousemove", this.onPanelMouseMove.bind(this));

    // The swatch radios are the only real control for this option, so the panel mirrors
    // whatever they hold, including the state the theme restores on a section re-render.
    this.fieldset.addEventListener("change", this.syncFromRadios);
    document.addEventListener("product:switched", this.onProductSwitched);

    this.syncFromRadios();

    window.addToCartButtonManager?.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    document.removeEventListener("product:switched", this.onProductSwitched);
    SizeDropdownReveal.stop(this);
  }

  onProductSwitched() {
    this.syncFromRadios();
    this.close();
  }

  get radios() {
    return Array.from(
      this.fieldset.querySelectorAll("input.custom-option-buttons"),
    );
  }

  radioForValue(value) {
    return this.radios.find((radio) => radio.value === value);
  }

  syncFromRadios() {
    const checked = this.radios.find((radio) => radio.checked);
    const value = checked ? checked.value : "";
    const hasValue = value !== "";

    this.valueEl.textContent = hasValue ? value : this.placeholder;
    this.classList.toggle("is-selected", hasValue);

    this.optionEls.forEach((optionEl, index) => {
      const isSelected = hasValue && optionEl.dataset.value === value;
      optionEl.setAttribute("aria-selected", isSelected ? "true" : "false");
      optionEl.classList.toggle("is-selected", isSelected);
      if (isSelected) this.activeIndex = index;
    });
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  onTriggerKeydown(event) {
    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault();
      this.open();
    }
  }

  onPanelClick(event) {
    const optionEl = event.target.closest('[role="option"]');
    if (!optionEl || !this.panel.contains(optionEl)) return;
    this.selectOption(optionEl);
  }

  onPanelMouseMove(event) {
    const optionEl = event.target.closest('[role="option"]');
    if (!optionEl) return;
    this.setActive(this.optionEls.indexOf(optionEl), false);
  }

  onPanelKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.setActive(this.activeIndex + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
        break;
      case "Home":
        event.preventDefault();
        this.setActive(0);
        break;
      case "End":
        event.preventDefault();
        this.setActive(this.optionEls.length - 1);
        break;
      case "Enter":
      case " ":
      case "Spacebar":
        event.preventDefault();
        if (this.optionEls[this.activeIndex])
          this.selectOption(this.optionEls[this.activeIndex]);
        break;
      case "Escape":
        event.preventDefault();
        this.close();
        this.trigger.focus({ preventScroll: true });
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  onDocumentPointerDown(event) {
    if (this.contains(event.target)) return;
    this.close();
  }

  setActive(index, scroll = true) {
    if (!this.optionEls.length) return;

    const bounded = Math.max(0, Math.min(index, this.optionEls.length - 1));
    this.activeIndex = bounded;

    this.optionEls.forEach((optionEl, i) =>
      optionEl.classList.toggle("is-active", i === bounded),
    );

    const activeEl = this.optionEls[bounded];
    this.panel.setAttribute("aria-activedescendant", activeEl.id);
    if (scroll) this.scrollActiveIntoView(activeEl);
  }

  scrollActiveIntoView(activeEl) {
    const panel = this.panel;
    if (panel.scrollHeight <= panel.clientHeight) return;

    const top = activeEl.offsetTop;
    const bottom = top + activeEl.offsetHeight;

    if (top < panel.scrollTop) {
      panel.scrollTop = top;
    } else if (bottom > panel.scrollTop + panel.clientHeight) {
      panel.scrollTop = bottom - panel.clientHeight;
    }
  }

  selectOption(optionEl) {
    const productUrl = optionEl.dataset.productUrl;
    if (productUrl) {
      window.location.href = productUrl;
      return;
    }

    const radio = this.radioForValue(optionEl.dataset.value);
    if (!radio) return;

    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));

    this.syncFromRadios();
    this.close();
    this.trigger.focus({ preventScroll: true });
  }

  // The "Size: 5 US | Size chart" row, so the label stays visible above the box.
  revealAnchor() {
    const label = this.fieldset.querySelector(":scope > .form__label");
    return label && label.getBoundingClientRect().height > 0 ? label : this;
  }

  // Where the page has to rest for the anchor to sit just under the pinned header.
  planReveal() {
    if (!SizeDropdownReveal.isRendered(this.trigger)) return null;

    const anchor = this.revealAnchor();
    const pinnedInset = SizeDropdownReveal.pinnedTopInset(this, anchor);
    const resolve = () => {
      if (!anchor.isConnected) return null;
      const restAt = pinnedInset() + SizeDropdownReveal.GAP;
      return SizeDropdownReveal.clampScroll(
        anchor.getBoundingClientRect().top + window.pageYOffset - restAt,
      );
    };
    const target = resolve();
    return target === null ? null : { resolve, target };
  }

  // On short screens, cap the list so its last row ends on screen once the page settles.
  fitPanel(settledScrollY) {
    const panel = this.panel;
    panel.style.removeProperty("max-height");

    const style = getComputedStyle(panel);
    const designMax = parseFloat(style.maxHeight);
    if (!Number.isFinite(designMax)) return;

    // max-height excludes padding and border unless the panel is border-box.
    const chrome =
      style.boxSizing === "border-box"
        ? 0
        : [
            "paddingTop",
            "paddingBottom",
            "borderTopWidth",
            "borderBottomWidth",
          ].reduce((sum, key) => sum + (parseFloat(style[key]) || 0), 0);

    // offsetTop ignores the open animation's transform, so this is the list's resting spot.
    const settledTop =
      this.getBoundingClientRect().top +
      panel.offsetTop -
      (settledScrollY - window.pageYOffset);
    const room = Math.floor(
      window.innerHeight - settledTop - SizeDropdownReveal.PANEL_GAP - chrome,
    );
    if (room < designMax)
      panel.style.maxHeight =
        Math.max(SizeDropdownReveal.PANEL_MIN, room) + "px";
  }

  startReveal(plan) {
    if (
      plan &&
      Math.abs(plan.target - window.pageYOffset) > SizeDropdownReveal.TOLERANCE
    ) {
      SizeDropdownReveal.glide(this, plan.resolve);
    }
  }

  // Public: bring an already-open list back into view (open() does this on its own).
  reveal() {
    if (!this.isOpen) {
      this.open();
      return;
    }
    const plan = this.planReveal();
    this.fitPanel(plan ? plan.target : window.pageYOffset);
    this.startReveal(plan);
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.panel.hidden = false;
    this.classList.add("is-open");
    this.trigger.setAttribute("aria-expanded", "true");

    const plan = this.planReveal();
    this.fitPanel(plan ? plan.target : window.pageYOffset);

    this.setActive(this.activeIndex < 0 ? 0 : this.activeIndex);
    this.panel.focus({ preventScroll: true });

    document.addEventListener("pointerdown", this.onDocumentPointerDown);

    this.startReveal(plan);
  }

  close() {
    SizeDropdownReveal.stop(this);
    if (!this.isOpen) return;

    this.isOpen = false;
    this.panel.hidden = true;
    this.panel.style.removeProperty("max-height");
    this.classList.remove("is-open");
    this.trigger.setAttribute("aria-expanded", "false");
    this.panel.removeAttribute("aria-activedescendant");
    this.optionEls.forEach((optionEl) =>
      optionEl.classList.remove("is-active"),
    );

    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
  }
}

if (!customElements.get("size-dropdown")) {
  customElements.define("size-dropdown", SizeDropdown);
}
